/**
 * CSRF — patrón double-submit cookie.
 *
 * Defensa en profundidad: las cookies de sesión ya tienen `sameSite: 'lax'`
 * (bloquea el envío en requests cross-site no-GET) y las rutas de negocio
 * reales viajan con `Authorization: Bearer <token>` directo al backend
 * Express (un atacante cross-site no puede leer ese token). El único lugar
 * donde un cookie por sí sola autoriza una acción es en estas rutas de
 * Next.js que mutan sesión (login/logout/refresh) — por eso se les agrega
 * este token adicional, no enviado automáticamente por el navegador.
 */

export const CSRF_COOKIE = 'csrf_token';
export const CSRF_HEADER = 'x-csrf-token';

/** Lee el valor de una cookie por nombre desde `document.cookie` (cliente). */
export function leerCookie(nombre: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${nombre}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Headers listos para agregar a un fetch que mute sesión (login/logout/refresh). */
export function csrfHeaders(): Record<string, string> {
  const token = leerCookie(CSRF_COOKIE);
  return token ? { [CSRF_HEADER]: token } : {};
}

/**
 * Verifica el patrón double-submit en una API route de Next.js (server-side).
 * Compara el header `x-csrf-token` contra la cookie `csrf_token` — solo
 * coinciden si la request salió de JS de este mismo origen, que es el único
 * que puede leer la cookie no-httpOnly y repetirla en un header custom.
 */
export function verificarCsrf(req: Request): boolean {
  const header = req.headers.get(CSRF_HEADER);
  const cookieHeader = req.headers.get('cookie') ?? '';
  const match = cookieHeader.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  const cookieToken = match ? decodeURIComponent(match[1]) : null;
  return !!header && !!cookieToken && header === cookieToken;
}
